import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./App.scss";
import AppLauncher, { LauncherGlyph } from "./components/AppLauncher";
import "./components/ScreenApps.scss";
import { evaluateCalculator, inputCalculator } from "./calculator";

const key = (label, code, units = 1, className = "") => ({
  label,
  code,
  units,
  className,
});

const keyboardRows = [
  [
    key("esc", "Escape", 1, "function-key"),
    key("F1", "F1", 1, "function-key"),
    key("F2", "F2", 1, "function-key"),
    key("F3", "F3", 1, "function-key"),
    key("F4", "F4", 1, "function-key"),
    key("F5", "F5", 1, "function-key"),
    key("F6", "F6", 1, "function-key"),
    key("F7", "F7", 1, "function-key"),
    key("F8", "F8", 1, "function-key"),
    key("F9", "F9", 1, "function-key"),
    key("F10", "F10", 1, "function-key"),
    key("F11", "F11", 1, "function-key"),
    key("F12", "F12", 1, "function-key"),
    key("del", "Delete", 1.35, "function-key"),
  ],
  [
    key("~", "Backquote"),
    key("1", "Digit1"),
    key("2", "Digit2"),
    key("3", "Digit3"),
    key("4", "Digit4"),
    key("5", "Digit5"),
    key("6", "Digit6"),
    key("7", "Digit7"),
    key("8", "Digit8"),
    key("9", "Digit9"),
    key("0", "Digit0"),
    key("-", "Minus"),
    key("=", "Equal"),
    key("backspace", "Backspace", 2, "wide-key"),
  ],
  [
    key("tab", "Tab", 1.5, "wide-key"),
    key("Q", "KeyQ"),
    key("W", "KeyW"),
    key("E", "KeyE"),
    key("R", "KeyR"),
    key("T", "KeyT"),
    key("Y", "KeyY"),
    key("U", "KeyU"),
    key("I", "KeyI"),
    key("O", "KeyO"),
    key("P", "KeyP"),
    key("[", "BracketLeft"),
    key("]", "BracketRight"),
    key("\\", "Backslash", 1.5, "wide-key"),
  ],
  [
    key("caps lock", "CapsLock", 1.75, "wide-key"),
    key("A", "KeyA"),
    key("S", "KeyS"),
    key("D", "KeyD"),
    key("F", "KeyF"),
    key("G", "KeyG"),
    key("H", "KeyH"),
    key("J", "KeyJ"),
    key("K", "KeyK"),
    key("L", "KeyL"),
    key(";", "Semicolon"),
    key("'", "Quote"),
    key("enter", "Enter", 2.25, "enter-key"),
  ],
  [
    key("shift", "ShiftLeft", 2.25, "wide-key"),
    key("Z", "KeyZ"),
    key("X", "KeyX"),
    key("C", "KeyC"),
    key("V", "KeyV"),
    key("B", "KeyB"),
    key("N", "KeyN"),
    key("M", "KeyM"),
    key(",", "Comma"),
    key(".", "Period"),
    key("/", "Slash"),
    key("shift", "ShiftRight", 2.75, "wide-key"),
  ],
  [
    key("ctrl", "ControlLeft", 1.25, "modifier-key"),
    key("fn", "Fn", 1.25, "modifier-key"),
    key("alt", "AltLeft", 1.25, "modifier-key"),
    key("space", "Space", 6.25, "space-key"),
    key("alt", "AltRight", 1.25, "modifier-key"),
    key("fn", "ContextMenu", 1.25, "modifier-key"),
    key("ctrl", "ControlRight", 1.25, "modifier-key"),
    key("←", "ArrowLeft", 1.25, "arrow-key"),
    key("↑", "ArrowUp", 1.25, "arrow-key"),
    key("↓", "ArrowDown", 1.25, "arrow-key"),
    key("→", "ArrowRight", 1.25, "arrow-key"),
  ],
];

const parameterConfig = {
  rt: { label: "RT", unit: "ms", min: 0.1, max: 2, step: 0.1 },
  actuation: { label: "ACTUATION", unit: "mm", min: 0.1, max: 4, step: 0.1 },
  reset: { label: "RAPID RESET", unit: "mm", min: 0.1, max: 2, step: 0.1 },
  deadzone: { label: "DEAD ZONE", unit: "mm", min: 0.1, max: 1.5, step: 0.1 },
};

const defaultParameters = {
  rt: 0.4,
  actuation: 1.8,
  reset: 0.3,
  deadzone: 0.5,
};
const allKeys = keyboardRows.flat();
const defaultLightingColors = [
  "#f04461",
  "#ff9b43",
  "#ffe04a",
  "#56dd90",
  "#27c9d5",
  "#397aff",
  "#a45cff",
];
const lightingCacheKey = "keysim-lighting-colors";
const lightingCacheVersion = 2;

function readLightingCache() {
  try {
    return JSON.parse(window.localStorage.getItem(lightingCacheKey) || "{}");
  } catch (error) {
    return {};
  }
}

function hexToRgb(hex) {
  const value = hex.replace("#", "");
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((value) => Math.round(value).toString(16).padStart(2, "0"))
    .join("")}`;
}

const screenApps = [
  { id: "settings", label: "设置", icon: "⚙", tone: "cyan" },
  { id: "key-profile", label: "键位参数", icon: "⌁", tone: "coral" },
  { id: "lighting", label: "灯光调节", icon: "☼", tone: "yellow" },
  { id: "wallpaper", label: "壁纸工坊", icon: "▧", tone: "violet" },
  { id: "music", label: "音乐播放", icon: "♫", tone: "orange" },
  { id: "realtime", label: "灵动副屏", icon: "⌁", tone: "mint" },
  { id: "numpad", label: "小键盘", icon: "▦", tone: "lime" },
  { id: "calculator", label: "计算器", icon: "⊞", tone: "pink" },
  { id: "touchpad", label: "触控板", icon: "⌖", tone: "blue" },
  { id: "tomato", label: "番茄钟", icon: "◷", tone: "amber" },
  { id: "pet", label: "萌宠乐园", icon: "♥", tone: "pet" },
];

const PET_ANIMATION_FPS = 8;
const PET_SPRITE_COLUMNS = 8;
const PET_SPRITE_FRAME_SIZE = 192;
const PET_SPRITE_ROW_PITCH = 210;
const petAnimationFrames = {
  idle: [0, 1, 2, 3, 4, 5],
  running: [8, 9, 10, 11, 12, 13, 14, 15],
  resting: [40, 41, 42, 41],
};

function ScreenStatusBar() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <div className="screen-statusbar">
      <strong>
        {now.toLocaleTimeString("zh-CN", {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </strong>
      <span className="status-symbols" aria-label="连接状态">
        <i>W</i>
        <i>▣</i>
        <i>▢</i>
        <i>A</i>
        <i>1</i>
      </span>
      <span className="status-battery">
        82%{" "}
        <i>
          <b />
        </i>
      </span>
    </div>
  );
}

const demoLyrics = [
  { time: 0, text: "配合你的性格" },
  { time: 12, text: "你的追求者 你的坎坷" },
  { time: 24, text: "我开的车" },
  { time: 34, text: "算一算 虚度了多少个年头" },
  { time: 46, text: "仿佛足够写一套错爱的春秋" },
  { time: 64, text: "如果以后 你还想为谁" },
  { time: 79, text: "浪费美好时候" },
  { time: 94, text: "眼泪只能在我的胸膛" },
  { time: 108, text: "毫无保留" },
  { time: 120, text: "互相折磨到白头" },
];

function formatTrackTime(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

function PetSprite({ activity, spritesheetUrl }) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [frameIndex, setFrameIndex] = useState(0);
  const frames = petAnimationFrames[activity] || petAnimationFrames.idle;
  const currentFrame = frames[frameIndex];

  useEffect(() => {
    const image = new window.Image();
    image.onload = () => setImageLoaded(true);
    image.src = spritesheetUrl;
    imageRef.current = image;
    return () => {
      image.onload = null;
      imageRef.current = null;
    };
  }, [spritesheetUrl]);

  useEffect(() => {
    setFrameIndex(0);
    const timer = window.setInterval(() => {
      setFrameIndex((index) => (index + 1) % frames.length);
    }, 1000 / PET_ANIMATION_FPS);
    return () => window.clearInterval(timer);
  }, [activity, frames.length]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !image || !imageLoaded || !context || currentFrame === undefined) return;

    const sourceX = (currentFrame % PET_SPRITE_COLUMNS) * PET_SPRITE_FRAME_SIZE;
    const sourceY = Math.floor(currentFrame / PET_SPRITE_COLUMNS) * PET_SPRITE_ROW_PITCH;
    context.clearRect(0, 0, PET_SPRITE_FRAME_SIZE, PET_SPRITE_FRAME_SIZE);
    context.imageSmoothingEnabled = true;
    context.drawImage(
      image,
      sourceX,
      sourceY,
      PET_SPRITE_FRAME_SIZE,
      PET_SPRITE_FRAME_SIZE,
      0,
      0,
      PET_SPRITE_FRAME_SIZE,
      PET_SPRITE_FRAME_SIZE
    );
  }, [currentFrame, imageLoaded]);

  return (
    <canvas
      ref={canvasRef}
      className="pet-sprite-canvas"
      width={PET_SPRITE_FRAME_SIZE}
      height={PET_SPRITE_FRAME_SIZE}
      aria-label="GPT-muse 动画"
    />
  );
}

function PetPlayground({ inputPulse }) {
  const routeRef = useRef(null);
  const progressRef = useRef(0);
  const facingRef = useRef(1);
  const activityTimers = useRef([]);
  const previousInputRef = useRef(inputPulse);
  const [position, setPosition] = useState({ x: 201, y: 97, facing: 1 });
  const [activity, setActivity] = useState("idle");
  const [stepCount, setStepCount] = useState(0);
  const trackUrl = `${process.env.PUBLIC_URL}/pets/runway.png`;
  const spritesheetUrl = `${process.env.PUBLIC_URL}/pets/gpt-muse-spritesheet.webp`;

  useEffect(() => {
    activityTimers.current.forEach((timer) => window.clearTimeout(timer));
    activityTimers.current = [
      window.setTimeout(() => setActivity("resting"), 3000),
    ];
    return () => activityTimers.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  useEffect(() => {
    if (inputPulse === previousInputRef.current) return;
    previousInputRef.current = inputPulse;
    const route = routeRef.current;
    if (!route) return;

    const totalLength = route.getTotalLength();
    if (!totalLength) return;
    const nextProgress = (progressRef.current + totalLength * 0.018) % totalLength;
    progressRef.current = nextProgress;
    const point = route.getPointAtLength(nextProgress);
    const atRouteEnd = nextProgress > totalLength - 10;
    const nearbyPoint = route.getPointAtLength(
      atRouteEnd ? nextProgress - 10 : nextProgress + 10
    );
    const directionX = atRouteEnd
      ? point.x - nearbyPoint.x
      : nearbyPoint.x - point.x;
    const facing = directionX < -1 ? -1 : directionX > 1 ? 1 : facingRef.current;
    facingRef.current = facing;
    setPosition({ x: point.x, y: point.y, facing });
    setStepCount((count) => count + 1);
    setActivity("running");
    activityTimers.current.forEach((timer) => window.clearTimeout(timer));
    activityTimers.current = [
      window.setTimeout(() => setActivity("idle"), 850),
      window.setTimeout(() => setActivity("resting"), 3000),
    ];
  }, [inputPulse]);

  useEffect(
    () => () => activityTimers.current.forEach((timer) => window.clearTimeout(timer)),
    []
  );

  return (
    <div className={`pet-playground is-${activity}`}>
      <div className="pet-scene-topline">
        <div><span>跑道小队</span><strong>GPT-muse 的散步时间</strong></div>
        <span className="pet-step-counter">{String(stepCount).padStart(2, "0")} 步</span>
      </div>
      <div className="pet-track-stage">
        <div className="pet-track-art">
          <img src={trackUrl} alt="萌宠跑道" draggable="false" />
          <svg className="pet-route-map" viewBox="0 0 941 873" aria-hidden="true">
            <path
              ref={routeRef}
              d="M 201 97 C 252 78 307 63 361 71 C 422 77 469 119 482 174 C 493 222 483 272 506 316 C 528 356 579 365 636 373 C 700 381 769 389 821 414 C 867 436 887 477 887 527 C 890 582 877 638 850 670 C 817 707 760 730 704 741 C 640 754 568 750 514 729 C 465 710 441 670 431 623 C 420 573 403 535 366 512 C 322 484 260 468 207 444 C 143 414 94 375 77 326 C 56 267 69 206 104 163 C 128 132 165 111 201 97 Z"
            />
          </svg>
          <div
            className="pet-runner"
            style={{
              left: `${(position.x / 941) * 100}%`,
              top: `${(position.y / 873) * 100}%`,
              transform: `translate(-50%, -94%) scaleX(${position.facing})`,
            }}
          >
            <PetSprite activity={activity} spritesheetUrl={spritesheetUrl} />
          </div>
        </div>
        <div className="pet-scene-sparkle sparkle-one">✦</div>
        <div className="pet-scene-sparkle sparkle-two">✧</div>
      </div>
      <div className="pet-status-card">
        <span className={`pet-status-dot is-${activity}`} />
        <div>
          <strong>{activity === "running" ? "跑起来啦！" : activity === "resting" ? "休息一下" : "沿跑道慢慢走"}</strong>
          <small>{activity === "resting" ? "按下任意按键唤醒萌宠" : "按下任意按键，让 GPT-muse 向前跑一段"}</small>
        </div>
        <span className="pet-status-icon">{activity === "resting" ? "☾" : activity === "running" ? "➜" : "♡"}</span>
      </div>
    </div>
  );
}

function MusicLyricsApp() {
  const duration = 312;
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(46);
  const [repeat, setRepeat] = useState(true);
  const [showLyrics, setShowLyrics] = useState(true);
  const lyricListRef = useRef(null);
  const activeLyricRef = useRef(null);
  const activeLyricIndex = demoLyrics.reduce(
    (active, lyric, index) => (progress >= lyric.time ? index : active),
    0
  );

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setProgress((current) => {
        if (current >= duration) return repeat ? 0 : duration;
        return current + 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing, repeat]);

  useEffect(() => {
    if (!showLyrics || !lyricListRef.current || !activeLyricRef.current) return;
    const list = lyricListRef.current;
    const line = activeLyricRef.current;
    list.scrollTo({
      top: line.offsetTop - list.offsetTop - list.clientHeight * 0.48,
      behavior: "smooth",
    });
  }, [activeLyricIndex, showLyrics]);

  return (
    <div className={`music-player ${showLyrics ? "lyrics-mode" : "cover-mode"}`}>
      <div className="music-track-info">
        <strong>不将就</strong>
        <span>李荣浩</span>
      </div>
      {showLyrics ? (
        <div className="music-lyrics" ref={lyricListRef}>
          {demoLyrics.map((lyric, index) => (
            <p
              className={`music-lyric ${index === activeLyricIndex ? "is-active" : ""}`}
              key={lyric.time}
              ref={index === activeLyricIndex ? activeLyricRef : null}
            >
              {lyric.text}
            </p>
          ))}
        </div>
      ) : (
        <div className="music-disc-view" aria-label="专辑封面模拟">
          <div className="music-disc"><i /></div>
          <strong>不将就</strong>
          <span>李荣浩</span>
        </div>
      )}
      <div className="music-controls">
        <input
          className="music-progress"
          type="range"
          min="0"
          max={duration}
          value={progress}
          aria-label="播放进度"
          onChange={(event) => setProgress(Number(event.currentTarget.value))}
        />
        <div className="music-time-row">
          <span>{formatTrackTime(progress)}</span>
          <span>{formatTrackTime(duration)}</span>
        </div>
        <div className="music-transport">
          <button
            type="button"
            className={repeat ? "is-enabled" : ""}
            aria-label={repeat ? "关闭循环播放" : "开启循环播放"}
            onClick={() => setRepeat(!repeat)}
          >
            ↻
          </button>
          <button type="button" aria-label="快退十秒" onClick={() => setProgress((current) => Math.max(0, current - 10))}>|◀</button>
          <button className="music-play-toggle" type="button" aria-label={playing ? "暂停" : "播放"} onClick={() => setPlaying(!playing)}>
            {playing ? "Ⅱ" : "▶"}
          </button>
          <button type="button" aria-label="快进十秒" onClick={() => setProgress((current) => Math.min(duration, current + 10))}>▶|</button>
          <button
            type="button"
            className={showLyrics ? "is-enabled" : ""}
            aria-label={showLyrics ? "显示专辑封面" : "显示歌词"}
            onClick={() => setShowLyrics(!showLyrics)}
          >
            ♬
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadGauge({ label, value, tone }) {
  return (
    <div className={`load-gauge ${tone}`}>
      <div className="load-gauge-heading">
        <span className="load-gauge-dot" />
        <strong>{label}</strong>
        <small>负载</small>
      </div>
      <svg className="load-gauge-dial" viewBox="0 0 160 104" role="img" aria-label={`${label} ${value}%`}>
        <defs>
          <linearGradient id={`gauge-${tone}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={tone === "cpu" ? "#39e7ff" : "#a69bff"} />
            <stop offset="100%" stopColor={tone === "cpu" ? "#59ffa6" : "#5e9dff"} />
          </linearGradient>
        </defs>
        <path className="load-gauge-track" d="M 12 84 A 68 68 0 0 1 148 84" pathLength="100" />
        <path
          className="load-gauge-value"
          d="M 12 84 A 68 68 0 0 1 148 84"
          pathLength="100"
          strokeDasharray={`${value} 100`}
        />
        <text className="load-gauge-number" x="80" y="69" textAnchor="middle">{value}%</text>
        <text className="load-gauge-unit" x="80" y="85" textAnchor="middle">{label} USAGE</text>
      </svg>
      <div className="load-gauge-trend" aria-label={`${label} 负载趋势示意`}>
        <div className="load-gauge-trend-bars" aria-hidden="true">
          {[-8, -4, -6, 1, -2, 5, 0, -3, 4, 2, -1, 0].map((offset, index) => (
            <i key={index} style={{ height: `${Math.max(12, value + offset)}%` }} />
          ))}
        </div>
        <div className="load-gauge-trend-caption"><span>负载趋势</span><span>60s</span></div>
      </div>
      <div className="load-gauge-details">
        <div><span>温度</span><strong>{tone === "cpu" ? 48 : 42}<small>°C</small></strong></div>
        <div><span>频率</span><strong>{tone === "cpu" ? "3.6" : "1.8"}<small>GHz</small></strong></div>
      </div>
    </div>
  );
}

function RealtimeModeControl({ portraitMode, onToggleMode, inline = false }) {
  return (
    <div className={`realtime-mode-control ${inline ? "is-inline" : ""}`}>
      <span>{portraitMode ? "横屏模式" : "竖屏模式"}</span>
      <button
        type="button"
        role="switch"
        aria-checked={portraitMode}
        aria-label={portraitMode ? "切换到横屏模式" : "切换到竖屏模式"}
        className={portraitMode ? "is-on" : ""}
        onClick={onToggleMode}
      >
        <i />
      </button>
    </div>
  );
}

function RealtimeDataApp({ portraitMode, onToggleMode }) {
  const [loads, setLoads] = useState({ CPU: 42, GPU: 38 });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setLoads((current) => ({
        CPU: Math.max(35, Math.min(49, current.CPU + Math.round(Math.random() * 4 - 2))),
        GPU: Math.max(31, Math.min(45, current.GPU + Math.round(Math.random() * 4 - 2))),
      }));
    }, 1400);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className={`realtime-page ${portraitMode ? "is-portrait" : ""}`}>
      <div className="realtime-subscreen">
        <img
          src={`${process.env.PUBLIC_URL}/images/${portraitMode ? "realtime-portrait-mode.png" : "realtime-subscreen-reference.png"}`}
          alt={portraitMode ? "竖屏模式副屏画面" : "副屏桌面仿真"}
        />
      </div>
      {portraitMode && (
        <RealtimeModeControl
          portraitMode={portraitMode}
          onToggleMode={onToggleMode}
        />
      )}
      {!portraitMode && (
        <div className="realtime-telemetry" aria-label="实时数据">
          <LoadGauge label="CPU" value={loads.CPU} tone="cpu" />
          <LoadGauge label="GPU" value={loads.GPU} tone="gpu" />
          <div className="realtime-system-summary">
            <div className="realtime-system-heading"><span>系统概览</span><span><i />运行平稳 · 演示</span></div>
            <div className="realtime-system-metrics">
              <div><span>内存</span><strong>8.2 <small>/ 16 GB</small></strong><i><b /></i></div>
              <div><span>风扇</span><strong>1,260 <small>RPM</small></strong><i><b /></i></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WallpaperStudio({ previewType, onPreviewChange }) {
  if (previewType) {
    const media = {
      image: (
        <img
          className="wallpaper-preview-image"
          src={`${process.env.PUBLIC_URL}/wallpapers/wallpaper-photo.jpg`}
          alt="导入的图片壁纸"
        />
      ),
      gif: (
        <img
          className="wallpaper-preview-gif"
          src={`${process.env.PUBLIC_URL}/wallpapers/wallpaper-animated.gif`}
          alt="导入的 GIF 壁纸"
        />
      ),
      video: (
        <video
          className="wallpaper-preview-video"
          src={`${process.env.PUBLIC_URL}/wallpapers/wallpaper-loop.mp4`}
          autoPlay
          loop
          muted
          playsInline
        />
      ),
    }[previewType];

    return (
      <div className={`wallpaper-preview wallpaper-preview-${previewType}`}>
        {media}
        <button
          className="wallpaper-preview-back"
          type="button"
          onClick={() => onPreviewChange(null)}
        >
          ‹ 返回
        </button>
      </div>
    );
  }

  return (
    <div className="wallpaper-studio">
      <div className="wallpaper-options">
        <button type="button" onClick={() => onPreviewChange("image")}>
          <span className="wallpaper-option-icon image-option-icon">▧</span>
          <span>选择导入的图片</span>
          <b>›</b>
        </button>
        <button type="button" onClick={() => onPreviewChange("gif")}>
          <span className="wallpaper-option-icon gif-option-icon">GIF</span>
          <span>选择导入的 GIF</span>
          <b>›</b>
        </button>
        <button type="button" onClick={() => onPreviewChange("video")}>
          <span className="wallpaper-option-icon video-option-icon">▶</span>
          <span>选择导入的视频</span>
          <b>›</b>
        </button>
      </div>
      <p>在电脑驱动页面先导入相关资源即可选择对应壁纸</p>
    </div>
  );
}

function NumpadApp({ onExit }) {
  const sequenceRef = useRef(0);
  const keyHandlerRef = useRef(null);
  keyHandlerRef.current = (key) => {
    const sequence = ["num", "divide", "multiply", "minus"];
    const expectedKey = sequence[sequenceRef.current];
    if (key === expectedKey) {
      sequenceRef.current += 1;
      if (sequenceRef.current === sequence.length) {
        sequenceRef.current = 0;
        onExit();
      }
    } else {
      sequenceRef.current = key === "num" ? 1 : 0;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const key = {
        NumLock: "num",
        NumpadDivide: "divide",
        Slash: "divide",
        NumpadMultiply: "multiply",
        NumpadSubtract: "minus",
        Minus: "minus",
      }[event.code] || {
        "/": "divide",
        "*": "multiply",
        "-": "minus",
        "−": "minus",
      }[event.key];
      if (key) {
        event.preventDefault();
        keyHandlerRef.current(key);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const keys = [
    { label: "Num", sub: "Lock", slot: "num-lock", tone: "dark" },
    { label: "/", slot: "divide", tone: "dark" },
    { label: "*", slot: "multiply", tone: "dark" },
    { label: "−", slot: "minus", tone: "dark" },
    { label: "7", sub: "Home", slot: "seven" },
    { label: "8", sub: "↑", slot: "eight" },
    { label: "9", sub: "PgUp", slot: "nine" },
    { label: "+", sub: "☼+", slot: "plus", tone: "function" },
    { label: "4", sub: "←", slot: "four" },
    { label: "5", sub: "◉", slot: "five" },
    { label: "6", sub: "→", slot: "six" },
    { label: "1", sub: "End", slot: "one" },
    { label: "2", sub: "↓", slot: "two" },
    { label: "3", sub: "PgDn", slot: "three" },
    { label: "Enter", sub: "☼−", slot: "enter", tone: "enter" },
    { label: "0", sub: "Insert", slot: "zero" },
    { label: ".", sub: "Delete", slot: "decimal" },
  ];

  return (
    <div className="numpad-screen">
      <div className="numpad-page">
        <div className="numpad-frame">
          <div className="numpad-grid" aria-label="立体数字小键盘">
            {keys.map((key) => (
              <button
                className={`numpad-key ${key.slot} ${key.tone || ""}`}
                type="button"
                key={key.slot}
                aria-label={`${key.label}${key.sub ? ` ${key.sub}` : ""}`}
                onClick={() => {
                  const sequenceKey = {
                    "num-lock": "num",
                    divide: "divide",
                    multiply: "multiply",
                    minus: "minus",
                  }[key.slot];
                  if (sequenceKey) keyHandlerRef.current(sequenceKey);
                }}
              >
                <span className="numpad-key-main">{key.label}</span>
                {key.sub && <span className="numpad-key-sub">{key.sub}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className="numpad-exit-hint">依次按下 Num、/、*、- 退出小键盘</p>
    </div>
  );
}

function DeviceScreen({ app, onOpen, onHome, portraitMode, onToggleMode, wallpaperPreviewType, children }) {
  const title = screenApps.find((item) => item.id === app)?.label || "键位参数";
  return (
    <div data-surface={app} className={`device-screen ${app === "home" ? "is-launcher" : "is-app"} ${app === "realtime" && portraitMode ? "is-realtime-portrait" : ""} ${app === "wallpaper" && wallpaperPreviewType ? "is-wallpaper-preview" : ""} ${app === "numpad" ? "is-numpad-fullscreen" : ""}`}>
      <ScreenStatusBar />
      {app === "home" ? (
        <AppLauncher apps={screenApps} onOpen={onOpen} />
      ) : (
        <>
          <div className="screen-page-heading">
            <button type="button" onClick={onHome} aria-label="返回主页">
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m11.5 5-5 5 5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span>主页</span>
            </button>
            <strong>{title}</strong>
            {app === "realtime" && !portraitMode ? (
              <RealtimeModeControl
                portraitMode={portraitMode}
                onToggleMode={onToggleMode}
                inline
              />
            ) : (
              <span />
            )}
          </div>
          <div className={`screen-page-content screen-page-content-${app}`}>{children}</div>
        </>
      )}
    </div>
  );
}

function SwitchSimulation({
  selectedKey,
  parameters,
  isPressed,
  isPreviewing,
  onPress,
  onRelease,
}) {
  const maxTravel = 4;
  const actuation = parameters.actuation || defaultParameters.actuation;
  const deadzone = parameters.deadzone || defaultParameters.deadzone;
  const reset = parameters.reset || defaultParameters.reset;
  const responseTime = 90 + (parameters.rt || defaultParameters.rt) * 120;
  const isActive = isPressed || isPreviewing;
  const travel = isActive
    ? Math.min(maxTravel, actuation + deadzone + 0.35)
    : 0.12;
  const actuationPosition = `${(actuation / maxTravel) * 100}%`;
  const deadzoneEnd = `${
    (Math.min(maxTravel, actuation + deadzone) / maxTravel) * 100
  }%`;
  const resetPosition = `${
    (Math.max(0, actuation - reset) / maxTravel) * 100
  }%`;
  return (
    <section
      className={`switch-simulation ${isPressed ? "is-pressed" : ""} ${
        isPreviewing && !isPressed ? "is-previewing" : ""
      }`}
      style={{
        "--travel-progress": `${(travel / maxTravel) * 100}%`,
        "--stem-offset": `${(travel / maxTravel) * 22}px`,
        "--response-time": `${responseTime}ms`,
        "--actuation-position": actuationPosition,
        "--deadzone-end": deadzoneEnd,
        "--reset-position": resetPosition,
      }}
      aria-label={`${selectedKey.label} 键轴行程仿真`}
    >
      <div className="simulation-caption">
        <span>单键行程仿真 / {selectedKey.label}</span>
        <b>
          {isPressed
            ? `${travel.toFixed(1)} mm · 已触发`
            : isPreviewing
            ? `${travel.toFixed(1)} mm · 参数预演`
            : "按住键帽预览"}
        </b>
      </div>
      <div className="switch-render-area">
        <div className="switch-light" />
        <div className="switch-3d" aria-hidden="true">
          <div className="switch-keycap">
            <i />
          </div>
          <div className="switch-stem">
            <i />
            <i />
            <i />
          </div>
          <div className="switch-coil">
            <i />
            <i />
            <i />
            <i />
            <i />
          </div>
          <div className="switch-housing">
            <i />
          </div>
          <div className="switch-base" />
        </div>
        <button
          className="simulation-press-target"
          type="button"
          aria-label="按住以模拟按键"
          onPointerDown={onPress}
          onPointerUp={onRelease}
          onPointerLeave={onRelease}
          onContextMenu={(event) => event.preventDefault()}
        />
        <div className="travel-scale" aria-label="按键行程示意">
          <span className="scale-label top">0</span>
          <span className="scale-label bottom">4 mm</span>
          <i className="travel-progress" />
          <i className="actuation-marker">
            <b>触发 {actuation.toFixed(1)}</b>
          </i>
          <i className="reset-marker">
            <b>复位 {Math.max(0, actuation - reset).toFixed(1)}</b>
          </i>
          <i className="deadzone-marker">
            <b>死区 {deadzone.toFixed(1)}</b>
          </i>
        </div>
      </div>
      <div className="simulation-legend">
        <span>
          <i className="legend-actuation" />
          触发点
        </span>
        <span>
          <i className="legend-reset" />
          复位点
        </span>
        <span>
          <i className="legend-deadzone" />
          死区区间
        </span>
        <span className="rt-readout">RT {responseTime.toFixed(0)} ms</span>
      </div>
    </section>
  );
}

function MultiKeySimulation({ keys, parameters }) {
  const count = keys.length;
  const columns =
    count <= 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : count <= 16 ? 4 : 5;
  const size = count <= 2 ? "large" : count <= 8 ? "medium" : "compact";
  const actuation = parameters.actuation || defaultParameters.actuation;
  const reset = parameters.reset || defaultParameters.reset;
  const deadzone = parameters.deadzone || defaultParameters.deadzone;
  const resetPosition = Math.max(0, actuation - reset);
  const deadzoneWidth = Math.min(4 - actuation, deadzone);
  return (
    <section
      className={`multi-key-preview size-${size}`}
      aria-label="已选按键示意图"
    >
      <div className="simulation-caption">
        <span>多键按键示意图</span>
        <b>{count ? `${count} 键已选 · 参数同步预览` : "请按键盘选择按键"}</b>
      </div>
      {count ? (
        <>
          <div className="multi-key-grid" style={{ "--key-columns": columns }}>
            {keys.map((item) => (
              <div
                className="multi-keycap"
                key={item.code}
                aria-label={`已选按键 ${item.label}`}
              >
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          <div className="multi-travel-display">
            <div className="multi-travel-title">
              <span>行程参数预览</span>
              <small>0 — 4 mm</small>
            </div>
            <div
              className="multi-travel-track"
              aria-label="触发、复位和死区位置"
            >
              <i
                className="multi-deadzone-zone"
                style={{
                  left: `${(actuation / 4) * 100}%`,
                  width: `${(deadzoneWidth / 4) * 100}%`,
                }}
              />
              <i
                className="multi-reset-point"
                style={{ left: `${(resetPosition / 4) * 100}%` }}
              />
              <i
                className="multi-actuation-point"
                style={{ left: `${(actuation / 4) * 100}%` }}
              />
            </div>
            <div className="multi-travel-values">
              <span className="reset-value">
                复位 {resetPosition.toFixed(1)}
              </span>
              <span className="actuation-value">
                触发 {actuation.toFixed(1)}
              </span>
              <span className="deadzone-value">死区 {deadzone.toFixed(1)}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="multi-key-empty">
          按下键盘按键后，选中的键位会显示在这里
        </div>
      )}
    </section>
  );
}

function HardwareKey({
  item,
  pressed,
  selected,
  lightColor,
  onPointerDown,
  onPointerUp,
}) {
  const isDown = pressed.has(item.code);
  const isSelected = selected.has(item.code);
  const classes = [
    "hardware-key",
    item.className,
    isDown ? "is-pressed" : "",
    isSelected ? "is-multi-selected" : "",
    lightColor ? "has-custom-light" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      className={classes}
      style={{ "--key-units": item.units, "--key-light-color": lightColor }}
      type="button"
      aria-label={item.label}
      aria-pressed={isDown || isSelected}
      onPointerDown={() => onPointerDown(item.code)}
      onPointerUp={() => onPointerUp(item.code)}
      onPointerLeave={() => onPointerUp(item.code)}
    >
      <span>{item.label}</span>
      <i aria-hidden="true" />
    </button>
  );
}

export default function App() {
  const [pressed, setPressed] = useState(() => new Set());
  const [lastInput, setLastInput] = useState("—");
  const [isListening, setIsListening] = useState(false);
  const [selectedCode, setSelectedCode] = useState("KeyA");
  const [multiKeyMode, setMultiKeyMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [activeApp, setActiveApp] = useState("home");
  const [petInputPulse, setPetInputPulse] = useState(0);
  const [realtimePortraitMode, setRealtimePortraitMode] = useState(false);
  const [wallpaperPreviewType, setWallpaperPreviewType] = useState(null);
  const [brightness, setBrightness] = useState(68);
  const [lightingColors, setLightingColors] = useState(() => {
    const cache = readLightingCache();
    const saved = cache.version === lightingCacheVersion ? cache.colors : null;
    return Array.isArray(saved) &&
      saved.length === defaultLightingColors.length &&
      saved.every((color) => /^#[0-9a-f]{6}$/i.test(color))
      ? saved
      : defaultLightingColors;
  });
  const [selectedLightingColor, setSelectedLightingColor] = useState(null);
  const [keyLightColors, setKeyLightColors] = useState({});
  const [lightingRate, setLightingRate] = useState(5);
  const [lightDirection, setLightDirection] = useState("right");
  const [tomatoMinutes, setTomatoMinutes] = useState(25);
  const [breakMinutes, setBreakMinutes] = useState(5);
  const [tomatoRunning, setTomatoRunning] = useState(false);
  const [calcValue, setCalcValue] = useState("0");
  const [previewActive, setPreviewActive] = useState(false);
  const previewTimer = useRef(null);
  const [parameters, setParameters] = useState(() => ({
    KeyA: { ...defaultParameters },
  }));

  const selectedKey =
    allKeys.find(
      (item) =>
        item.code === selectedCode &&
        (!multiKeyMode || selectedKeys.has(item.code))
    ) ||
    (multiKeyMode && allKeys.find((item) => selectedKeys.has(item.code))) ||
    allKeys[0];
  const selectedParameters = parameters[selectedKey.code] || defaultParameters;

  useEffect(() => () => window.clearTimeout(previewTimer.current), []);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        lightingCacheKey,
        JSON.stringify({
          version: lightingCacheVersion,
          colors: lightingColors,
        })
      );
    } catch (error) {
      // Keep the lighting controls usable when browser storage is unavailable.
    }
  }, [lightingColors]);

  const applySelectedLightingColor = useCallback(
    (code) => {
      if (selectedLightingColor === null) return;
      setKeyLightColors((current) => ({
        ...current,
        [code]: lightingColors[selectedLightingColor],
      }));
    },
    [selectedLightingColor, lightingColors]
  );

  const setLightingChannel = (channel, value) => {
    if (selectedLightingColor === null) return;
    setLightingColors((current) =>
      current.map((color, index) => {
        if (index !== selectedLightingColor) return color;
        const next = hexToRgb(color);
        next[channel] = Math.max(0, Math.min(255, value));
        return rgbToHex(next);
      })
    );
  };

  const previewParameterChange = () => {
    setPreviewActive(true);
    window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(
      () => setPreviewActive(false),
      480
    );
  };

  const updateParameterValue = (name, value) => {
    const targets = multiKeyMode
      ? Array.from(selectedKeys)
      : [selectedKey.code];
    setParameters((current) => {
      const next = { ...current };
      targets.forEach((code) => {
        next[code] = {
          ...(current[code] || defaultParameters),
          [name]: value,
        };
      });
      return next;
    });
    previewParameterChange();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const isEditableTarget =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.closest("input, textarea, select, [contenteditable='true']"));
      if (isEditableTarget || event.isComposing) return;
      if (
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
      }

      const code = event.code || "unknown";
      if (!event.repeat) setPetInputPulse((pulse) => pulse + 1);
      setIsListening(true);
      setLastInput(code);
      if (allKeys.some((item) => item.code === code)) {
        setSelectedCode(code);
        applySelectedLightingColor(code);
        if (multiKeyMode && !event.repeat) {
          setSelectedKeys((current) => {
            const next = new Set(current);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
          });
        }
      }
      setPressed((current) => {
        if (current.has(code)) return current;
        const next = new Set(current);
        next.add(code);
        return next;
      });
    };

    const handleKeyUp = (event) => {
      const code = event.code || "unknown";
      setPressed((current) => {
        if (!current.has(code)) return current;
        const next = new Set(current);
        next.delete(code);
        return next;
      });
    };

    const handleBlur = () => setPressed(new Set());

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [multiKeyMode, applySelectedLightingColor]);

  const pressedCount = pressed.size;
  const deckLabel = useMemo(
    () =>
      pressedCount
        ? `${pressedCount} key${pressedCount > 1 ? "s" : ""} down`
        : "ready for input",
    [pressedCount]
  );

  const pressVirtualKey = (code, toggleSelection = true) => {
    setPetInputPulse((pulse) => pulse + 1);
    setIsListening(true);
    setLastInput(code);
    setSelectedCode(code);
    applySelectedLightingColor(code);
    if (multiKeyMode && toggleSelection) {
      setSelectedKeys((current) => {
        const next = new Set(current);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        return next;
      });
    }
    setPressed((current) => new Set(current).add(code));
  };

  const releaseVirtualKey = (code) => {
    setPressed((current) => {
      const next = new Set(current);
      next.delete(code);
      return next;
    });
  };

  const returnToHome = () => {
    if (activeApp === "lighting") {
      setSelectedLightingColor(null);
      setKeyLightColors({});
    }
    if (activeApp === "key-profile") {
      setMultiKeyMode(false);
      setSelectedKeys(new Set());
    }
    setRealtimePortraitMode(false);
    setWallpaperPreviewType(null);
    setActiveApp("home");
  };

  const changeCalculator = (value) => {
    if (value === "=") {
      try {
        setCalcValue(evaluateCalculator(calcValue));
      } catch (error) {
        setCalcValue("Error");
      }
      return;
    }
    setCalcValue((current) => inputCalculator(current, value));
  };

  const renderScreenApp = () => {
    if (activeApp === "pet") return <PetPlayground inputPulse={petInputPulse} />;
    if (activeApp === "key-profile")
      return (
        <div className="screen-profile">
          {multiKeyMode ? (
            <MultiKeySimulation
              keys={allKeys.filter((item) => selectedKeys.has(item.code))}
              parameters={selectedParameters}
            />
          ) : (
            <SwitchSimulation
              selectedKey={selectedKey}
              parameters={selectedParameters}
              isPressed={pressed.has(selectedKey.code)}
              isPreviewing={previewActive}
              onPress={() => pressVirtualKey(selectedKey.code, false)}
              onRelease={() => releaseVirtualKey(selectedKey.code)}
            />
          )}
          <div className="screen-tuning-console">
            <div className="tuning-console-heading">
              <strong>按键参数</strong>
              <div className="tuning-heading-actions">
                {multiKeyMode && <span>已选 {selectedKeys.size} 键</span>}
                <label className="multi-key-toggle">
                  <input
                    type="checkbox"
                    checked={multiKeyMode}
                    onChange={(event) => {
                      const enabled = event.currentTarget.checked;
                      setMultiKeyMode(enabled);
                      setSelectedKeys(
                        enabled ? new Set([selectedKey.code]) : new Set()
                      );
                    }}
                  />
                  <span>多键修改</span>
                </label>
              </div>
            </div>
            {Object.keys(parameterConfig).map((name) => {
              const config = parameterConfig[name];
              const value = selectedParameters[name] || defaultParameters[name];
              return (
                <div className="screen-tune-row" key={name}>
                  <div className="tune-label">
                    <span>
                      {name === "actuation"
                        ? "触发点"
                        : name === "reset"
                        ? "快速复位"
                        : name === "deadzone"
                        ? "死区"
                        : "快速触发"}
                    </span>
                    <strong>
                      {value.toFixed(1)} <small>{config.unit}</small>
                    </strong>
                  </div>
                  <button
                    type="button"
                    aria-label={`减少${config.label}`}
                    disabled={multiKeyMode && selectedKeys.size === 0}
                    onClick={() => adjustParameter(name, -1)}
                  >
                    −
                  </button>
                  <input
                    aria-label={config.label}
                    type="range"
                    min={config.min}
                    max={config.max}
                    step={config.step}
                    value={value}
                    disabled={multiKeyMode && selectedKeys.size === 0}
                    onChange={(event) => {
                      const nextValue = Number(event.currentTarget.value);
                      updateParameterValue(name, nextValue);
                    }}
                  />
                  <button
                    type="button"
                    aria-label={`增加${config.label}`}
                    disabled={multiKeyMode && selectedKeys.size === 0}
                    onClick={() => adjustParameter(name, 1)}
                  >
                    +
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      );
    if (activeApp === "settings")
      return (
        <div className="screen-settings-list">
          {[
            "连接方式",
            "系统切换",
            "按键回报率",
            "按键消抖时间",
            "时间调节",
            "番茄闹钟",
            "声音与触感",
            "语言选择",
            "关于本机",
          ].map((item, index) => (
            <button type="button" key={item}>
              <i className={`setting-icon setting-${index}`}>
                <LauncherGlyph id={["touchpad", "settings", "numpad", "settings", "tomato", "tomato", "music", "settings", "settings"][index]} />
              </i>
              <span>{item}</span><b className="setting-chevron" aria-hidden="true">›</b>
            </button>
          ))}
        </div>
      );
    if (activeApp === "lighting")
      return (
        <div className="lighting-page">
          <h3>灯光效果</h3>
          <div className="effect-chips">
            {[
              "随波逐流",
              "峰回路转",
              "涟漪扩散",
              "川流不息",
              "一触即发",
              "静态恒亮",
              "繁星点点",
              "漫天飞雪",
              "单点亮",
              "单点灭",
              "动态呼吸",
              "光谱循环",
            ].map((x) => (
              <button type="button" key={x}>
                {x}
              </button>
            ))}
          </div>
          <div className="lighting-color-heading">
            <h3>灯光颜色</h3>
          </div>
          <div className="color-dots">
            {lightingColors.map((color, index) => (
              <button
                type="button"
                key={index}
                className={selectedLightingColor === index ? "selected" : ""}
                style={{ background: color }}
                aria-label={`选择颜色 ${index + 1}`}
                aria-pressed={selectedLightingColor === index}
                onClick={() => setSelectedLightingColor(index)}
              />
            ))}
          </div>
          {selectedLightingColor !== null && (
            <div className="rgb-controls">
              <div className="rgb-spinners">
                {Object.entries(
                  hexToRgb(lightingColors[selectedLightingColor])
                ).map(([channel, value]) => (
                  <label
                    className={`rgb-slider-row rgb-${channel}`}
                    key={channel}
                  >
                    <span>{channel.toUpperCase()}</span>
                    <input
                      type="range"
                      min="0"
                      max="255"
                      step="1"
                      value={value}
                      onChange={(event) =>
                        setLightingChannel(
                          channel,
                          Number(event.currentTarget.value)
                        )
                      }
                      aria-label={`${channel.toUpperCase()} 通道值`}
                    />
                    <output aria-live="polite">{value}</output>
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className="brightness-control">
            ☼ 亮度
            <input
              type="range"
              min="0"
              max="100"
              value={brightness}
              onChange={(event) => setBrightness(Number(event.target.value))}
            />
            {brightness}%
          </label>
          <div className="lighting-motion-controls">
            <label className="rate-slider">
              <span>◷ 速率</span>
              <input
                type="range"
                min="1"
                max="10"
                step="1"
                value={lightingRate}
                onChange={(event) =>
                  setLightingRate(Number(event.currentTarget.value))
                }
                aria-label="灯光速率"
              />
              <output aria-live="polite">{lightingRate}</output>
              <small>级</small>
            </label>
            <div
              className="direction-control"
              role="group"
              aria-label="灯光方向"
            >
              <span>✥ 方向</span>
              <button
                className={lightDirection === "left" ? "selected" : ""}
                type="button"
                aria-pressed={lightDirection === "left"}
                onClick={() => setLightDirection("left")}
              >
                向左
              </button>
              <button
                className={lightDirection === "right" ? "selected" : ""}
                type="button"
                aria-pressed={lightDirection === "right"}
                onClick={() => setLightDirection("right")}
              >
                向右
              </button>
            </div>
          </div>
        </div>
      );
    if (activeApp === "tomato")
      return (
        <div className="tomato-page">
          <div className="tomato-session-summary">
            <div className="tomato-summary-copy">
              <span><i /> POMODORO SESSION</span>
              <strong>{tomatoRunning ? "专注进行中" : "准备开始专注"}</strong>
              <small>专注 {tomatoMinutes} 分钟 · 休息 {breakMinutes} 分钟</small>
            </div>
            <div
              className="tomato-clock-ring"
              style={{ "--duration-progress": `${(tomatoMinutes / 60) * 100}%` }}
            >
              <div><strong>{tomatoMinutes}</strong><small>MIN</small></div>
            </div>
          </div>
          <div className="tomato-duration-card focus-duration-card">
            <div className="duration-heading">
              <span><i />专注时长</span>
              <strong>{tomatoMinutes} 分钟</strong>
            </div>
            <div className="duration-slider-wrap">
              <span>5 分钟</span>
              <input
                type="range"
                min="5"
                max="60"
                step="5"
                value={tomatoMinutes}
                aria-label="专注时长，5 到 60 分钟"
                onChange={(event) => setTomatoMinutes(Number(event.currentTarget.value))}
              />
              <span>60 分钟</span>
            </div>
          </div>
          <div className="tomato-duration-card break-card">
            <div className="duration-heading">
              <span><i />休息时长</span>
              <strong>{breakMinutes} 分钟</strong>
            </div>
            <div className="duration-slider-wrap">
              <span>1 分钟</span>
              <input
                type="range"
                min="1"
                max="30"
                step="1"
                value={breakMinutes}
                aria-label="休息时长，1 到 30 分钟"
                onChange={(event) => setBreakMinutes(Number(event.currentTarget.value))}
              />
              <span>30 分钟</span>
            </div>
          </div>
          <button
            className="tomato-start-button"
            type="button"
            onClick={() => setTomatoRunning(!tomatoRunning)}
          >
            <span>{tomatoRunning ? "Ⅱ" : "▶"}</span>
            {tomatoRunning ? "暂停专注" : "开始专注"}
          </button>
        </div>
      );
    if (activeApp === "calculator")
      return (
        <div className="calculator-page">
          <div className="calculator-display">
            <span>CALCULATOR <i /> BASIC</span>
            <output aria-live="polite">{calcValue}</output>
          </div>
          <div className="calculator-keys">
            {[
              "C",
              "÷",
              "×",
              "⌫",
              "7",
              "8",
              "9",
              "+",
              "4",
              "5",
              "6",
              "−",
              "1",
              "2",
              "3",
              "=",
              "+/−",
              "0",
              ".",
            ].map((value) => (
              <button
                type="button"
                className={`calculator-key ${/[÷×+−=⌫]/.test(value) ? "operator" : "number"} ${value === "C" ? "clear" : ""} ${value === "=" ? "equals" : ""}`}
                key={value}
                aria-label={value === "+/−" ? "切换正负号" : value}
                onClick={() =>
                  changeCalculator(
                    { "÷": "/", "×": "*", "−": "-" }[value] || value
                  )
                }
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      );
    if (activeApp === "realtime")
      return (
        <RealtimeDataApp
          portraitMode={realtimePortraitMode}
          onToggleMode={() => setRealtimePortraitMode((current) => !current)}
        />
      );
    if (activeApp === "wallpaper")
      return (
        <WallpaperStudio
          previewType={wallpaperPreviewType}
          onPreviewChange={setWallpaperPreviewType}
        />
      );
    if (activeApp === "music") return <MusicLyricsApp />;
    if (activeApp === "numpad") return <NumpadApp onExit={returnToHome} />;
    return (
      <div className="placeholder-page">
        <div className="touchpad-surface" aria-hidden="true">
          <LauncherGlyph id="touchpad" /><i className="touchpad-contact" /><div className="touchpad-buttons"><i /><i /></div>
        </div>
        <strong>触控板</strong>
        <p>触控区域模拟</p>
      </div>
    );
  };

  const adjustParameter = (name, direction) => {
    const config = parameterConfig[name];
    const targets = multiKeyMode
      ? Array.from(selectedKeys)
      : [selectedKey.code];
    setParameters((current) => {
      const next = { ...current };
      targets.forEach((code) => {
        const currentKey = current[code] || defaultParameters;
        const value = Math.min(
          config.max,
          Math.max(
            config.min,
            Number((currentKey[name] + config.step * direction).toFixed(1))
          )
        );
        next[code] = { ...currentKey, [name]: value };
      });
      return next;
    });
    previewParameterChange();
  };

  return (
    <main className="prototype-app">
      <div className="ambient ambient-left" />
      <div className="ambient ambient-right" />

      <header className="prototype-header">
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <div>
            <p className="eyebrow">HARDWARE STUDY / 01</p>
            <h1>
              KEYBOARD<span>+</span>DISPLAY
            </h1>
          </div>
        </div>
        <div className="header-meta">
          <span className={`signal-dot ${isListening ? "is-live" : ""}`} />
          <span>USB INPUT {isListening ? "ACTIVE" : "STANDBY"}</span>
          <span className="header-divider" />
          <span>PROTOTYPE 480 × 800</span>
        </div>
      </header>

      <section
        className="prototype-stage"
        aria-label="Keyboard with integrated display prototype"
      >
        <div className="product-frame">
          <section className="keyboard-deck" aria-label="Virtual keyboard">
            <div className="deck-topline">
              <span>MK–01 / LOW PROFILE</span>
              <span className="deck-state">{deckLabel}</span>
            </div>
            <div className="keyboard-layout">
              {keyboardRows.map((row, rowIndex) => (
                <div
                  className={`key-row key-row-${rowIndex + 1}`}
                  key={rowIndex}
                >
                  {row.map((item) => (
                    <HardwareKey
                      key={`${item.code}-${item.label}`}
                      item={item}
                      pressed={pressed}
                      selected={selectedKeys}
                      lightColor={keyLightColors[item.code]}
                      onPointerDown={pressVirtualKey}
                      onPointerUp={releaseVirtualKey}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className="deck-bottomline">
              <span>CHASSIS / BRUSHED GRAPHITE</span>
              <span>LAST SIGNAL / {lastInput}</span>
            </div>
          </section>

          <section
            className="display-deck"
            aria-label="Keyboard secondary display"
          >
            <div className="display-glass">
              <div className="glass-sheen" aria-hidden="true" />
              <DeviceScreen
                app={activeApp}
                onOpen={setActiveApp}
                onHome={returnToHome}
                portraitMode={realtimePortraitMode}
                onToggleMode={() => setRealtimePortraitMode((current) => !current)}
                wallpaperPreviewType={wallpaperPreviewType}
              >
                {renderScreenApp()}
              </DeviceScreen>
            </div>
            <div className="display-spec">
              <span>ACTIVE SURFACE</span>
              <span>480 × 800 PX</span>
            </div>
          </section>
        </div>
      </section>

      <footer className="prototype-footer">
        <span>PHYSICAL INPUT MIRRORED IN REAL TIME</span>
        <span className="footer-crosshair" aria-hidden="true">
          ＋
        </span>
        <span>INTERACTION LAYER / 00</span>
      </footer>
    </main>
  );
}
